import React, { useState, useEffect, useCallback } from 'react';
import { transportAPI } from '../../services/api';
import { confirmDialog } from '../../utils/notify';

const TransportRoutesTab = () => {
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Details Modal & Inner Tab State
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [activeDetailTab, setActiveDetailTab] = useState('overview'); // 'overview' | 'edit_stops' | 'assign_vehicle' | 'assign_student'
  const [selectedRoute, setSelectedRoute] = useState(null);

  // Submitting States
  const [submitting, setSubmitting] = useState(false);
  const [assigningSubmitting, setAssigningSubmitting] = useState(false);

  // Vehicle Assignment State
  const [eligibleVehicles, setEligibleVehicles] = useState([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState('');
  const [loadingEligibleVehicles, setLoadingEligibleVehicles] = useState(false);
  const [isEditingVehicleAssignment, setIsEditingVehicleAssignment] = useState(false);
  const [isVehicleDropdownOpen, setIsVehicleDropdownOpen] = useState(false);
  const [vehicleSearchText, setVehicleSearchText] = useState('');

  // Multi-Student Assignment State
  const [studentSearchQuery, setStudentSearchQuery] = useState('');
  const [studentSearchResults, setStudentSearchResults] = useState([]);
  const [searchingStudents, setSearchingStudents] = useState(false);
  const [selectedStudents, setSelectedStudents] = useState([]); // Array of selected student objects
  const [selectedRouteStopId, setSelectedRouteStopId] = useState('');
  const [assigningStudentSubmitting, setAssigningStudentSubmitting] = useState(false);
  const [assignedStudents, setAssignedStudents] = useState([]);
  const [loadingAssignedStudents, setLoadingAssignedStudents] = useState(false);

  // Form State for Creating / Editing Route Stops
  const [formData, setFormData] = useState({
    route_number: '',
    route_name: '',
    starting_point: 'Campus',
    status: 'ACTIVE',
    stops: [
      { stop_order: 1, destination: '', distance_km: '' }
    ]
  });

  const loadRoutes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await transportAPI.getRoutes();
      setRoutes(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading routes:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load routes.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRoutes();
  }, [loadRoutes]);

  // Load Assigned Students for Selected Route
  const loadRouteAssignedStudents = useCallback(async (routeId) => {
    if (!routeId) return;
    try {
      setLoadingAssignedStudents(true);
      const res = await transportAPI.getRouteAssignedStudents(routeId);
      setAssignedStudents(res.assigned_students || []);
    } catch (err) {
      console.error('Error loading assigned students:', err);
    } finally {
      setLoadingAssignedStudents(false);
    }
  }, []);

  // Real-time Smooth Student Search (No page reload)
  const handleSearchStudents = useCallback(async (query) => {
    try {
      setSearchingStudents(true);
      const res = await transportAPI.searchStudentsForTransport(query);
      setStudentSearchResults(Array.isArray(res) ? res : []);
    } catch (err) {
      console.error('Error searching students:', err);
    } finally {
      setSearchingStudents(false);
    }
  }, []);

  useEffect(() => {
    if (activeDetailTab === 'assign_student') {
      handleSearchStudents(studentSearchQuery);
    }
  }, [studentSearchQuery, activeDetailTab, handleSearchStudents]);

  useEffect(() => {
    if (activeDetailTab === 'assign_student' && selectedRoute) {
      loadRouteAssignedStudents(selectedRoute.id);
    }
  }, [activeDetailTab, selectedRoute, loadRouteAssignedStudents]);

  // Open Details Modal for an existing Route
  const handleOpenDetailsModal = async (route, tab = 'overview') => {
    setSelectedRoute(route);
    setActiveDetailTab(tab);
    setError(null);

    // Reset vehicle & editing state
    setIsEditingVehicleAssignment(!route.assigned_vehicle);
    setIsVehicleDropdownOpen(false);
    setVehicleSearchText('');
    setSelectedVehicleId('');

    // Reset multi-student assignment state
    setSelectedStudents([]);
    setSelectedRouteStopId('');
    setStudentSearchQuery('');

    // Pre-fill form data for editing
    setFormData({
      route_number: route.route_number || '',
      route_name: route.route_name || '',
      starting_point: route.starting_point || 'Campus',
      status: route.status || 'ACTIVE',
      stops: route.stops && route.stops.length > 0
        ? route.stops.map((s, idx) => ({
            stop_order: s.stop_order || idx + 1,
            destination: s.destination || '',
            distance_km: s.distance_km || ''
          }))
        : [{ stop_order: 1, destination: '', distance_km: '' }]
    });

    setShowDetailsModal(true);

    // Fetch eligible vehicles for route assignment
    try {
      setLoadingEligibleVehicles(true);
      const res = await transportAPI.getEligibleVehiclesForRoute(route.id);
      setEligibleVehicles(res.eligible_vehicles || []);
    } catch (err) {
      console.error('Error loading eligible vehicles:', err);
    } finally {
      setLoadingEligibleVehicles(false);
    }
  };

  // Open Details Modal in "Create Route" Mode
  const handleOpenCreateRouteModal = () => {
    const count = routes.length + 1;
    const nextRouteNum = `R-${String(count).padStart(3, '0')}`;
    setSelectedRoute(null);
    setActiveDetailTab('edit_stops');
    setError(null);
    setFormData({
      route_number: nextRouteNum,
      route_name: '',
      starting_point: 'Campus',
      status: 'ACTIVE',
      stops: [
        { stop_order: 1, destination: '', distance_km: '' }
      ]
    });
    setEligibleVehicles([]);
    setSelectedVehicleId('');
    setIsEditingVehicleAssignment(true);
    setIsVehicleDropdownOpen(false);
    setVehicleSearchText('');
    setShowDetailsModal(true);
  };

  // Toggle Checkbox Selection for Multiple Students
  const handleToggleStudentSelection = (student) => {
    setSelectedStudents(prev => {
      const exists = prev.some(s => s.id === student.id);
      if (exists) {
        return prev.filter(s => s.id !== student.id);
      } else {
        return [...prev, student];
      }
    });
  };

  // Stop Builder Handlers
  const handleAddStop = () => {
    setFormData(prev => ({
      ...prev,
      stops: [
        ...prev.stops,
        { stop_order: prev.stops.length + 1, destination: '', distance_km: '' }
      ]
    }));
  };

  const handleRemoveStop = (index) => {
    setFormData(prev => {
      const updated = prev.stops.filter((_, idx) => idx !== index);
      const reindexed = updated.map((s, idx) => ({ ...s, stop_order: idx + 1 }));
      return { ...prev, stops: reindexed };
    });
  };

  const handleMoveStop = (index, direction) => {
    setFormData(prev => {
      const newStops = [...prev.stops];
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= newStops.length) return prev;
      
      const temp = newStops[index];
      newStops[index] = newStops[targetIndex];
      newStops[targetIndex] = temp;

      const reindexed = newStops.map((s, idx) => ({ ...s, stop_order: idx + 1 }));
      return { ...prev, stops: reindexed };
    });
  };

  const handleStopChange = (index, field, value) => {
    setFormData(prev => {
      const newStops = [...prev.stops];
      newStops[index] = { ...newStops[index], [field]: value };
      return { ...prev, stops: newStops };
    });
  };

  // Submit Route Form (Create or Edit)
  const handleSaveRouteSubmit = async (e) => {
    e.preventDefault();
    if (!formData.route_name.trim()) {
      setError('Route Name is required.');
      return;
    }

    const validStops = formData.stops
      .filter(s => s.destination.trim() !== '')
      .map((s, idx) => ({
        stop_order: idx + 1,
        destination: s.destination.trim(),
        distance_km: parseFloat(s.distance_km) || 0
      }));

    if (validStops.length === 0) {
      setError('At least one valid destination/stop is required.');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const payload = {
        route_number: formData.route_number.trim(),
        route_name: formData.route_name.trim(),
        starting_point: formData.starting_point.trim() || 'Campus',
        status: formData.status,
        stops: validStops
      };

      let updatedRoute = null;
      if (selectedRoute && selectedRoute.id) {
        const res = await transportAPI.updateRoute(selectedRoute.id, payload);
        updatedRoute = res.route || res;
        setSuccessMsg(`Route ${payload.route_number} updated successfully.`);
      } else {
        const res = await transportAPI.createRoute(payload);
        updatedRoute = res.route || res;
        setSuccessMsg(`Route ${payload.route_number} created successfully.`);
      }

      await loadRoutes();
      if (updatedRoute) {
        setSelectedRoute(updatedRoute);
        setActiveDetailTab('overview');
      }
      setTimeout(() => setSuccessMsg(null), 4000);
    } catch (err) {
      console.error('Error saving route:', err);
      setError(err.response?.data?.error || err.message || 'Failed to save route.');
    } finally {
      setSubmitting(false);
    }
  };

  // Submit Vehicle Assignment
  const handleAssignVehicleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedVehicleId || !selectedRoute) return;

    try {
      setAssigningSubmitting(true);
      setError(null);
      const res = await transportAPI.assignVehicleToRoute(selectedRoute.id, selectedVehicleId);
      setSuccessMsg(res.message || 'Vehicle assigned successfully to route.');

      const updatedRoute = res.route || res;
      await loadRoutes();
      if (updatedRoute) {
        setSelectedRoute(updatedRoute);
      }
      setIsEditingVehicleAssignment(false);
      setActiveDetailTab('overview');
      setTimeout(() => setSuccessMsg(null), 4000);
    } catch (err) {
      console.error('Error assigning vehicle to route:', err);
      setError(err.response?.data?.error || err.message || 'Failed to assign vehicle.');
    } finally {
      setAssigningSubmitting(false);
    }
  };

  // Remove Vehicle Assignment
  const handleUnassignVehicle = async (route) => {
    if (!route || !route.assigned_vehicle) return;
    const confirmText = `Remove Vehicle Assignment?\n\nVehicle ${route.assigned_vehicle.registration_number} is currently assigned to Route ${route.route_number}.\nRemoving this assignment will leave the route without an active vehicle.`;
    if (!await confirmDialog(confirmText)) return;

    try {
      setError(null);
      const res = await transportAPI.unassignVehicleFromRoute(route.id);
      setSuccessMsg(res.message || `Vehicle assignment removed for route ${route.route_number}.`);

      const updatedRoute = res.route || res;
      await loadRoutes();
      if (updatedRoute) {
        setSelectedRoute(updatedRoute);
      }
      setIsEditingVehicleAssignment(true);
      setTimeout(() => setSuccessMsg(null), 4000);
    } catch (err) {
      console.error('Error unassigning vehicle from route:', err);
      setError(err.response?.data?.error || err.message || 'Failed to remove vehicle assignment.');
    }
  };

  // Delete Route
  const handleDeleteRoute = async (route) => {
    if (!route) return;
    if (!await confirmDialog(`Are you sure you want to delete route ${route.route_number}?`)) return;
    try {
      setError(null);
      await transportAPI.deleteRoute(route.id);
      setSuccessMsg(`Route ${route.route_number} deleted.`);
      setShowDetailsModal(false);
      loadRoutes();
      setTimeout(() => setSuccessMsg(null), 4000);
    } catch (err) {
      console.error('Error deleting route:', err);
      setError(err.response?.data?.error || err.message || 'Failed to delete route.');
    }
  };

  // Direct Submit Multiple Student Assignments
  const handleAssignMultipleStudentsSubmit = async (e) => {
    e.preventDefault();
    if (!selectedRoute || selectedStudents.length === 0 || !selectedRouteStopId) return;

    try {
      setAssigningStudentSubmitting(true);
      setError(null);

      let successCount = 0;
      const errorsList = [];

      for (const stu of selectedStudents) {
        try {
          await transportAPI.assignStudentToRoute(selectedRoute.id, stu.id, selectedRouteStopId);
          successCount++;
        } catch (err) {
          errorsList.push(`${stu.full_name}: ${err.response?.data?.error || err.message}`);
        }
      }

      if (successCount > 0) {
        setSuccessMsg(`Successfully assigned ${successCount} student(s) to Route ${selectedRoute.route_number}.`);
      }
      if (errorsList.length > 0) {
        setError(`Failed to assign some students:\n${errorsList.join('\n')}`);
      }

      setSelectedStudents([]);
      setSelectedRouteStopId('');
      loadRouteAssignedStudents(selectedRoute.id);
      setTimeout(() => setSuccessMsg(null), 5000);
    } catch (err) {
      console.error('Error assigning multiple students:', err);
      setError(err.response?.data?.error || err.message || 'Failed to assign students.');
    } finally {
      setAssigningStudentSubmitting(false);
    }
  };

  // Unassign Student
  const handleUnassignStudent = async (assignmentId, studentName) => {
    if (!await confirmDialog(`Are you sure you want to remove transport assignment for student "${studentName}"?`)) return;
    try {
      setError(null);
      const res = await transportAPI.unassignStudentFromRoute(assignmentId);
      setSuccessMsg(res.message || `Student assignment removed.`);
      loadRouteAssignedStudents(selectedRoute.id);
      setTimeout(() => setSuccessMsg(null), 4000);
    } catch (err) {
      console.error('Error unassigning student:', err);
      setError(err.response?.data?.error || err.message || 'Failed to remove student assignment.');
    }
  };

  const filteredRoutes = routes.filter(r =>
    (r.route_number || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (r.route_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (r.stops || []).some(s => (s.destination || '').toLowerCase().includes(searchQuery.toLowerCase())) ||
    (r.assigned_vehicle?.registration_number || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const selectedVehicleObj = eligibleVehicles.find(v => String(v.id) === String(selectedVehicleId));

  return (
    <div className="tm-card" style={{ marginTop: 20 }}>
      {/* Header Bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16, marginBottom: 20 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0f172a' }}>
            <span>🗺️</span> Transport Routes & Stops
          </h3>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>
            Manage transport routes, multi-destination stops, vehicle assignments, and student transport enrollment.
          </p>
        </div>
        <button
          onClick={handleOpenCreateRouteModal}
          className="driver-btn-primary"
          style={{ width: 'auto', padding: '10px 20px', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 8 }}
        >
          <span>➕</span> Add New Route
        </button>
      </div>

      {/* Notifications */}
      {error && (
        <div className="alert alert-danger" style={{ marginBottom: 16, borderRadius: 10, padding: '12px 16px', background: '#fff1f2', border: '1px solid #fecdd3', color: '#9f1239', fontSize: 13, whiteSpace: 'pre-line' }}>
          <span>⚠️</span> {error}
        </div>
      )}
      {successMsg && (
        <div className="alert alert-success" style={{ marginBottom: 16, borderRadius: 10, padding: '12px 16px', background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#047857', fontSize: 13 }}>
          <span>✓</span> {successMsg}
        </div>
      )}

      {/* Search Filter */}
      <div style={{ marginBottom: 20 }}>
        <input
          type="text"
          placeholder="🔍 Search routes by Route ID, Route Name, or Stop destination (e.g. Kazipet)..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="driver-input"
          style={{ maxWidth: 480 }}
        />
      </div>

      {/* Simplified Routes Roster Table */}
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading routes...</div>
      ) : filteredRoutes.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', background: '#f8fafc', borderRadius: 12, border: '1px dashed #cbd5e1' }}>
          No routes found matching search. Click <strong>"+ Add New Route"</strong> to create a new route.
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Route ID</th>
                <th>Route Name</th>
                <th>Route Path Preview (Distance from Campus)</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRoutes.map(r => (
                <tr key={r.id}>
                  <td>
                    <span className="badge badge-blue" style={{ fontSize: 12, fontWeight: 700 }}>
                      {r.route_number}
                    </span>
                  </td>
                  <td>
                    <strong>{r.route_name}</strong>
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                      Start: {r.starting_point || 'Campus'}
                    </div>
                  </td>
                  <td style={{ maxWidth: 480 }}>
                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6, fontSize: 12 }}>
                      <span style={{ background: '#e2e8f0', color: '#334155', padding: '2px 8px', borderRadius: 6, fontWeight: 600 }}>
                        {r.starting_point || 'Campus'}
                      </span>
                      {(r.stops || []).map(s => (
                        <React.Fragment key={s.id || s.stop_order}>
                          <span style={{ color: '#94a3b8' }}>➔</span>
                          <span style={{ background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af', padding: '2px 8px', borderRadius: 6 }}>
                            {s.destination} ({s.distance_km} KM)
                          </span>
                        </React.Fragment>
                      ))}
                      <span style={{ color: '#94a3b8' }}>➔</span>
                      <span style={{ background: '#e2e8f0', color: '#334155', padding: '2px 8px', borderRadius: 6, fontWeight: 600 }}>
                        {r.starting_point || 'Campus'}
                      </span>
                    </div>
                  </td>
                  <td>
                    <button
                      onClick={() => handleOpenDetailsModal(r, 'overview')}
                      className="btn-sm btn-primary"
                      style={{ padding: '6px 14px', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 6, background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}
                    >
                      👁️ Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ─── DEDICATED ROUTE DETAILS MODAL (WITH INNER TABS & EXPANDED SIZE) ─── */}
      {showDetailsModal && (
        <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div className="modal-content" style={{ background: '#fff', borderRadius: 16, width: '95vw', maxWidth: 1150, maxHeight: '94vh', overflowY: 'auto', padding: 28, boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}>
            
            {/* Modal Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '1px solid #e2e8f0', paddingBottom: 16, marginBottom: 16 }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#0f172a' }}>
                    {selectedRoute ? `Route Details — ${selectedRoute.route_number}` : 'Create New Route'}
                  </h3>
                  {selectedRoute && (
                    <span className={`badge-status ${selectedRoute.status === 'ACTIVE' ? 'verified' : 'rejected'}`} style={{ fontSize: 11 }}>
                      {selectedRoute.status}
                    </span>
                  )}
                </div>
                {selectedRoute && (
                  <div style={{ fontSize: 13, color: '#64748b', marginTop: 4, fontWeight: 600 }}>
                    {selectedRoute.route_name}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {selectedRoute && (
                  <button
                    onClick={() => handleDeleteRoute(selectedRoute)}
                    style={{ padding: '6px 12px', background: '#fff1f2', border: '1px solid #fecdd3', color: '#e11d48', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                  >
                    🗑️ Delete Route
                  </button>
                )}
                <button onClick={() => setShowDetailsModal(false)} style={{ border: 'none', background: 'transparent', fontSize: 22, cursor: 'pointer', color: '#64748b' }}>
                  &times;
                </button>
              </div>
            </div>

            {/* Inner Tabs Bar */}
            <div style={{ display: 'flex', gap: 8, borderBottom: '2px solid #e2e8f0', marginBottom: 20, flexWrap: 'wrap' }}>
              {selectedRoute && (
                <button
                  type="button"
                  onClick={() => setActiveDetailTab('overview')}
                  style={{
                    padding: '8px 16px',
                    border: 'none',
                    borderBottom: activeDetailTab === 'overview' ? '3px solid #2563eb' : '3px solid transparent',
                    background: 'transparent',
                    color: activeDetailTab === 'overview' ? '#1d4ed8' : '#64748b',
                    fontWeight: activeDetailTab === 'overview' ? 700 : 500,
                    fontSize: 13,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6
                  }}
                >
                  📍 Overview & Stops
                </button>
              )}

              <button
                type="button"
                onClick={() => setActiveDetailTab('edit_stops')}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderBottom: activeDetailTab === 'edit_stops' ? '3px solid #2563eb' : '3px solid transparent',
                  background: 'transparent',
                  color: activeDetailTab === 'edit_stops' ? '#1d4ed8' : '#64748b',
                  fontWeight: activeDetailTab === 'edit_stops' ? 700 : 500,
                  fontSize: 13,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6
                }}
              >
                ✏️ Edit Route & Stops
              </button>

              {selectedRoute && (
                <button
                  type="button"
                  onClick={() => setActiveDetailTab('assign_vehicle')}
                  style={{
                    padding: '8px 16px',
                    border: 'none',
                    borderBottom: activeDetailTab === 'assign_vehicle' ? '3px solid #2563eb' : '3px solid transparent',
                    background: 'transparent',
                    color: activeDetailTab === 'assign_vehicle' ? '#1d4ed8' : '#64748b',
                    fontWeight: activeDetailTab === 'assign_vehicle' ? 700 : 500,
                    fontSize: 13,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6
                  }}
                >
                  🚌 Assign Vehicle
                </button>
              )}

              {selectedRoute && (
                <button
                  type="button"
                  onClick={() => setActiveDetailTab('assign_student')}
                  style={{
                    padding: '8px 16px',
                    border: 'none',
                    borderBottom: activeDetailTab === 'assign_student' ? '3px solid #2563eb' : '3px solid transparent',
                    background: 'transparent',
                    color: activeDetailTab === 'assign_student' ? '#1d4ed8' : '#64748b',
                    fontWeight: activeDetailTab === 'assign_student' ? 700 : 500,
                    fontSize: 13,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6
                  }}
                >
                  🎓 Assign Student
                </button>
              )}
            </div>

            {/* TAB 1: OVERVIEW & STOPS */}
            {activeDetailTab === 'overview' && selectedRoute && (
              <div>
                {/* Operational Summary Grid */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
                  <div style={{ background: '#f8fafc', padding: 16, borderRadius: 12, border: '1px solid #e2e8f0' }}>
                    <h4 style={{ margin: '0 0 10px', fontSize: 14, fontWeight: 700, color: '#334155' }}>
                      📋 Route Information
                    </h4>
                    <div style={{ fontSize: 13, display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <div><span style={{ color: '#64748b' }}>Route ID:</span> <strong>{selectedRoute.route_number}</strong></div>
                      <div><span style={{ color: '#64748b' }}>Route Name:</span> <strong>{selectedRoute.route_name}</strong></div>
                      <div><span style={{ color: '#64748b' }}>Starting Point:</span> <strong>{selectedRoute.starting_point || 'Campus'}</strong></div>
                      <div><span style={{ color: '#64748b' }}>Total Destinations:</span> <strong>{selectedRoute.stops ? selectedRoute.stops.length : 0} Stops</strong></div>
                    </div>
                  </div>

                  {/* Assigned Vehicle & Driver Summary Card */}
                  <div style={{ background: selectedRoute.assigned_vehicle ? '#eff6ff' : '#fff7ed', padding: 16, borderRadius: 12, border: selectedRoute.assigned_vehicle ? '1px solid #bfdbfe' : '1px solid #ffedd5' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: selectedRoute.assigned_vehicle ? '#1e40af' : '#c2410c' }}>
                        🚌 Assigned Vehicle & Driver
                      </h4>
                      {selectedRoute.assigned_vehicle ? (
                        <button
                          type="button"
                          onClick={() => handleUnassignVehicle(selectedRoute)}
                          style={{ padding: '3px 8px', fontSize: 11, background: '#fff1f2', color: '#e11d48', border: '1px solid #fecdd3', borderRadius: 6, cursor: 'pointer' }}
                        >
                          ❌ Unassign
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setActiveDetailTab('assign_vehicle')}
                          style={{ padding: '4px 10px', fontSize: 11, background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}
                        >
                          ➕ Assign
                        </button>
                      )}
                    </div>

                    {selectedRoute.assigned_vehicle ? (
                      <div style={{ fontSize: 13, color: '#1e293b', display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <div><strong>Vehicle Reg:</strong> {selectedRoute.assigned_vehicle.registration_number}</div>
                        <div><strong>Type & Capacity:</strong> {selectedRoute.assigned_vehicle.vehicle_type_display} ({selectedRoute.assigned_vehicle.seating_capacity} Seats)</div>
                        <div style={{ marginTop: 4, paddingTop: 6, borderTop: '1px solid #dbeafe', color: '#15803d', fontWeight: 600 }}>
                          🧑‍✈️ Driver: {selectedRoute.assigned_vehicle.assigned_driver ? `${selectedRoute.assigned_vehicle.assigned_driver.driver_name} (${selectedRoute.assigned_vehicle.assigned_driver.employee_id || 'ID N/A'})` : 'No driver assigned to vehicle'}
                        </div>
                      </div>
                    ) : (
                      <div style={{ fontSize: 12, color: '#9a3412', marginTop: 4 }}>
                        No vehicle assigned to operate this route. Click <strong>"Assign"</strong> to assign an active vehicle.
                      </div>
                    )}
                  </div>
                </div>

                {/* Destinations & Stops Table */}
                <div style={{ marginBottom: 20 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
                      Destinations & Stop Distance Breakdown
                    </h4>
                    <button
                      type="button"
                      onClick={() => setActiveDetailTab('edit_stops')}
                      style={{ padding: '4px 12px', background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1d4ed8', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                    >
                      ✏️ Edit Stops
                    </button>
                  </div>

                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', textAlign: 'left', color: '#64748b', fontSize: 12, borderBottom: '1px solid #e2e8f0' }}>
                        <th style={{ padding: '8px 12px' }}>Stop #</th>
                        <th style={{ padding: '8px 12px' }}>Destination Name</th>
                        <th style={{ padding: '8px 12px' }}>Distance from College (KM)</th>
                        <th style={{ padding: '8px 12px' }}>Matching Pricing Slab</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedRoute.stops || []).length === 0 ? (
                        <tr>
                          <td colSpan="4" style={{ padding: 16, textAlign: 'center', color: '#94a3b8' }}>
                            No stops added to this route.
                          </td>
                        </tr>
                      ) : (
                        (selectedRoute.stops || []).map((s, idx) => (
                          <tr key={s.id || idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '10px 12px', fontWeight: 700, color: '#475569' }}>
                              Stop #{s.stop_order || idx + 1}
                            </td>
                            <td style={{ padding: '10px 12px', fontWeight: 600, color: '#0f172a' }}>
                              {s.destination}
                            </td>
                            <td style={{ padding: '10px 12px', fontWeight: 600, color: '#1e40af' }}>
                              {s.distance_km} KM
                            </td>
                            <td style={{ padding: '10px 12px' }}>
                              {s.matching_slab ? (
                                <span style={{ background: '#dcfce7', color: '#166534', fontSize: 11, padding: '2px 8px', borderRadius: 6, fontWeight: 700 }}>
                                  {s.matching_slab.slab_name} ({s.matching_slab.min_distance}–{s.matching_slab.max_distance} KM)
                                </span>
                              ) : (
                                <span style={{ color: '#94a3b8', fontSize: 11 }}>N/A</span>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Route Live Path Preview Box */}
                <div style={{ padding: 14, background: '#f0fdf4', borderRadius: 10, border: '1px solid #bbf7d0' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#166534', marginBottom: 6 }}>
                    📍 Visual Route Path:
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, fontSize: 13, color: '#15803d', fontWeight: 600 }}>
                    <span>{selectedRoute.starting_point || 'Campus'}</span>
                    {(selectedRoute.stops || []).map((s, i) => (
                      <React.Fragment key={i}>
                        <span>➔</span>
                        <span style={{ background: '#ffffff', padding: '2px 10px', borderRadius: 6, border: '1px solid #86efac' }}>
                          {s.destination} ({s.distance_km} KM)
                        </span>
                      </React.Fragment>
                    ))}
                    <span>➔</span>
                    <span>{selectedRoute.starting_point || 'Campus'}</span>
                  </div>
                </div>
              </div>
            )}

            {/* TAB 2: EDIT ROUTE & STOPS */}
            {activeDetailTab === 'edit_stops' && (
              <form onSubmit={handleSaveRouteSubmit}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: 12 }} className="driver-form-group">
                  <div>
                    <label className="driver-label">Route ID *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. R-001"
                      value={formData.route_number}
                      onChange={e => setFormData({ ...formData, route_number: e.target.value.toUpperCase() })}
                      className="driver-input"
                    />
                  </div>
                  <div>
                    <label className="driver-label">Route Name *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Campus - Hanamkonda - Kazipet - Warangal"
                      value={formData.route_name}
                      onChange={e => setFormData({ ...formData, route_name: e.target.value })}
                      className="driver-input"
                    />
                  </div>
                  <div>
                    <label className="driver-label">Status</label>
                    <select
                      value={formData.status}
                      onChange={e => setFormData({ ...formData, status: e.target.value })}
                      className="driver-input"
                    >
                      <option value="ACTIVE">Active</option>
                      <option value="INACTIVE">Inactive</option>
                    </select>
                  </div>
                </div>

                <div className="driver-form-group">
                  <label className="driver-label">Starting Point</label>
                  <input
                    type="text"
                    placeholder="Campus"
                    value={formData.starting_point}
                    onChange={e => setFormData({ ...formData, starting_point: e.target.value })}
                    className="driver-input"
                  />
                </div>

                {/* Dynamic Destinations / Stops Section */}
                <div style={{ marginTop: 24, padding: 16, background: '#f8fafc', borderRadius: 12, border: '1px solid #e2e8f0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
                      Destinations / Stops (Measured from College)
                    </h4>
                    <button
                      type="button"
                      onClick={handleAddStop}
                      style={{ padding: '6px 12px', background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1d4ed8', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                    >
                      ➕ Add Destination
                    </button>
                  </div>

                  {formData.stops.map((stop, idx) => (
                    <div key={idx} style={{ display: 'grid', gridTemplateColumns: 'auto 2fr 1.5fr auto', gap: 10, alignItems: 'center', marginBottom: 10, padding: 10, background: '#fff', borderRadius: 8, border: '1px solid #cbd5e1' }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#475569', minWidth: 60 }}>
                        Stop #{idx + 1}
                      </div>
                      <div>
                        <input
                          type="text"
                          required
                          placeholder="Destination Name e.g. Hanamkonda"
                          value={stop.destination}
                          onChange={e => handleStopChange(idx, 'destination', e.target.value)}
                          className="driver-input"
                          style={{ margin: 0 }}
                        />
                      </div>
                      <div>
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          required
                          placeholder="Distance from College (KM)"
                          value={stop.distance_km}
                          onChange={e => handleStopChange(idx, 'distance_km', e.target.value)}
                          className="driver-input"
                          style={{ margin: 0 }}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button
                          type="button"
                          onClick={() => handleMoveStop(idx, -1)}
                          disabled={idx === 0}
                          title="Move Up"
                          style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #cbd5e1', background: '#f1f5f9', cursor: idx === 0 ? 'not-allowed' : 'pointer' }}
                        >
                          ⬆️
                        </button>
                        <button
                          type="button"
                          onClick={() => handleMoveStop(idx, 1)}
                          disabled={idx === formData.stops.length - 1}
                          title="Move Down"
                          style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #cbd5e1', background: '#f1f5f9', cursor: idx === formData.stops.length - 1 ? 'not-allowed' : 'pointer' }}
                        >
                          ⬇️
                        </button>
                        {formData.stops.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveStop(idx)}
                            title="Delete Stop"
                            style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #fecdd3', background: '#fff1f2', color: '#e11d48', cursor: 'pointer' }}
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Route Live Preview Box */}
                <div style={{ marginTop: 20, padding: 14, background: '#f0fdf4', borderRadius: 10, border: '1px solid #bbf7d0' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#166534', marginBottom: 6 }}>
                    📍 Live Route Preview:
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, fontSize: 13, color: '#15803d', fontWeight: 600 }}>
                    <span>{formData.starting_point || 'Campus'}</span>
                    {formData.stops.map((s, i) => (
                      <React.Fragment key={i}>
                        <span>➔</span>
                        <span style={{ background: '#ffffff', padding: '2px 10px', borderRadius: 6, border: '1px solid #86efac' }}>
                          {s.destination || `Stop #${i + 1}`} ({s.distance_km || 0} KM from College)
                        </span>
                      </React.Fragment>
                    ))}
                    <span>➔</span>
                    <span>{formData.starting_point || 'Campus'}</span>
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24, borderTop: '1px solid #e2e8f0', paddingTop: 16 }}>
                  <button
                    type="button"
                    onClick={() => setShowDetailsModal(false)}
                    style={{ padding: '10px 20px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="driver-btn-primary"
                    style={{ width: 'auto', padding: '10px 24px' }}
                  >
                    {submitting ? 'Saving Route...' : selectedRoute ? 'Update Route & Stops' : 'Save & Finalize Route'}
                  </button>
                </div>
              </form>
            )}

            {/* TAB 3: ASSIGN VEHICLE */}
            {activeDetailTab === 'assign_vehicle' && selectedRoute && (() => {
              const filteredEligibleVehicles = eligibleVehicles.filter(v =>
                (v.registration_number || '').toLowerCase().includes(vehicleSearchText.toLowerCase()) ||
                (v.vehicle_type_display || v.vehicle_type || '').toLowerCase().includes(vehicleSearchText.toLowerCase()) ||
                (v.make || '').toLowerCase().includes(vehicleSearchText.toLowerCase()) ||
                (v.model || '').toLowerCase().includes(vehicleSearchText.toLowerCase())
              );

              const assignedVehicle = selectedRoute.assigned_vehicle;
              const isLocked = assignedVehicle && !isEditingVehicleAssignment;

              return (
                <div>
                  {/* Header Banner */}
                  <div style={{ background: '#f8fafc', padding: 14, borderRadius: 10, border: '1px solid #e2e8f0', marginBottom: 20, fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: 700, color: '#0f172a' }}>📍 {selectedRoute.route_number} — {selectedRoute.route_name}</div>
                      <div style={{ color: '#64748b', fontSize: 12, marginTop: 4 }}>
                        Start: {selectedRoute.starting_point || 'Campus'} | Status: <strong>{selectedRoute.status}</strong>
                      </div>
                    </div>
                    {assignedVehicle && !isEditingVehicleAssignment && (
                      <button
                        type="button"
                        onClick={() => setIsEditingVehicleAssignment(true)}
                        style={{ padding: '6px 14px', background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1d4ed8', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
                      >
                        ✏️ Edit Vehicle Assignment
                      </button>
                    )}
                  </div>

                  {/* READ-ONLY LOCKED VIEW WHEN VEHICLE IS ASSIGNED AND NOT IN EDITING MODE */}
                  {isLocked ? (
                    <div style={{ background: '#eff6ff', padding: 20, borderRadius: 14, border: '1px solid #bfdbfe', marginBottom: 20 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                        <div style={{ fontWeight: 700, color: '#1e40af', fontSize: 15, display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span>🔒</span> <span>Assigned Vehicle & Driver Details</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setIsEditingVehicleAssignment(true)}
                          style={{ padding: '6px 14px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
                        >
                          ✏️ Edit
                        </button>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 13, color: '#1e293b' }}>
                        <div><strong>Registration Number:</strong> <span style={{ color: '#1d4ed8', fontWeight: 700 }}>{assignedVehicle.registration_number}</span></div>
                        <div><strong>Vehicle Type:</strong> {assignedVehicle.vehicle_type_display}</div>
                        <div><strong>Seating Capacity:</strong> {assignedVehicle.seating_capacity} Seats</div>
                        <div><strong>Status:</strong> <span className="badge badge-green" style={{ fontSize: 11 }}>{assignedVehicle.status_display || 'ACTIVE'}</span></div>
                      </div>

                      <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #bfdbfe', fontSize: 13 }}>
                        <strong>Assigned Driver:</strong>{' '}
                        {(assignedVehicle.assigned_driver || assignedVehicle.driver) ? (
                          <span style={{ color: '#15803d', fontWeight: 700 }}>
                            🧑‍✈️ {(assignedVehicle.assigned_driver || assignedVehicle.driver).driver_name || (assignedVehicle.assigned_driver || assignedVehicle.driver).name} ({(assignedVehicle.assigned_driver || assignedVehicle.driver).employee_id || 'ID N/A'})
                          </span>
                        ) : (
                          <span style={{ color: '#64748b' }}>No driver assigned to vehicle</span>
                        )}
                      </div>

                      <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
                        <button
                          type="button"
                          onClick={() => handleUnassignVehicle(selectedRoute)}
                          style={{ padding: '6px 14px', background: '#fff1f2', border: '1px solid #fecdd3', color: '#e11d48', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                        >
                          ❌ Remove Vehicle Assignment
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* EDITABLE VIEW (FORM WITH INTEGRATED SEARCHABLE DROPDOWN) */
                    <form onSubmit={handleAssignVehicleSubmit}>
                      {assignedVehicle && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, background: '#fff7ed', border: '1px solid #ffedd5', padding: '10px 14px', borderRadius: 8, color: '#9a3412', fontSize: 13 }}>
                          <span>✏️ <strong>Editing Vehicle Assignment:</strong> Select a new vehicle to replace vehicle {assignedVehicle.registration_number}.</span>
                          <button
                            type="button"
                            onClick={() => setIsEditingVehicleAssignment(false)}
                            style={{ padding: '4px 10px', background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}
                          >
                            Cancel Edit
                          </button>
                        </div>
                      )}

                      <div className="driver-form-group" style={{ marginBottom: 20 }}>
                        <label className="driver-label">Select Operating Vehicle (Search Integrated in Dropdown) *</label>

                        {/* Searchable Vehicle Picker Component */}
                        <div style={{ position: 'relative' }}>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              border: '1px solid #cbd5e1',
                              borderRadius: 8,
                              background: '#fff',
                              padding: '4px 12px'
                            }}
                          >
                            <span style={{ fontSize: 14, color: '#64748b', marginRight: 8 }}>🔍</span>
                            <input
                              type="text"
                              placeholder="Click or type registration number, vehicle type, or model to search..."
                              value={
                                isVehicleDropdownOpen
                                  ? vehicleSearchText
                                  : selectedVehicleObj
                                  ? `${selectedVehicleObj.registration_number} — ${selectedVehicleObj.vehicle_type_display} (${selectedVehicleObj.seating_capacity} seats)`
                                  : vehicleSearchText
                              }
                              onFocus={() => {
                                setIsVehicleDropdownOpen(true);
                                if (selectedVehicleObj) setVehicleSearchText('');
                              }}
                              onChange={e => {
                                setVehicleSearchText(e.target.value);
                                setIsVehicleDropdownOpen(true);
                              }}
                              onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}
                              style={{
                                border: 'none',
                                outline: 'none',
                                width: '100%',
                                padding: '8px 0',
                                fontSize: 13,
                                color: '#0f172a',
                                background: 'transparent'
                              }}
                            />
                            {selectedVehicleId ? (
                              <button
                                type="button"
                                onClick={() => {
                                  setSelectedVehicleId('');
                                  setVehicleSearchText('');
                                  setIsVehicleDropdownOpen(true);
                                }}
                                style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#94a3b8', fontSize: 16, padding: '0 4px' }}
                              >
                                ✕
                              </button>
                            ) : (
                              <span style={{ color: '#64748b', fontSize: 12 }}>▼</span>
                            )}
                          </div>

                          {/* Dropdown Overlay Panel */}
                          {isVehicleDropdownOpen && (
                            <div
                              style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                right: 0,
                                marginTop: 4,
                                maxHeight: 220,
                                overflowY: 'auto',
                                background: '#fff',
                                border: '1px solid #cbd5e1',
                                borderRadius: 8,
                                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                                zIndex: 100
                              }}
                            >
                              {loadingEligibleVehicles ? (
                                <div style={{ padding: 12, fontSize: 13, color: '#64748b', textAlign: 'center' }}>Loading eligible active vehicles...</div>
                              ) : filteredEligibleVehicles.length === 0 ? (
                                <div style={{ padding: 12, fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>
                                  No matching eligible vehicles found.
                                </div>
                              ) : (
                                filteredEligibleVehicles.map(v => (
                                  <div
                                    key={v.id}
                                    onClick={() => {
                                      setSelectedVehicleId(String(v.id));
                                      setIsVehicleDropdownOpen(false);
                                      setVehicleSearchText('');
                                    }}
                                    style={{
                                      padding: '10px 14px',
                                      borderBottom: '1px solid #f1f5f9',
                                      cursor: 'pointer',
                                      background: String(v.id) === String(selectedVehicleId) ? '#eff6ff' : '#fff',
                                      display: 'flex',
                                      justify: 'space-between',
                                      alignItems: 'center'
                                    }}
                                  >
                                    <div>
                                      <strong style={{ color: '#0f172a', fontSize: 13 }}>{v.registration_number}</strong>
                                      <span style={{ color: '#64748b', fontSize: 12, marginLeft: 8 }}>
                                        {v.vehicle_type_display} ({v.seating_capacity} seats)
                                      </span>
                                      {(v.assigned_driver || v.driver) && (
                                        <div style={{ fontSize: 11, color: '#15803d', marginTop: 2 }}>
                                          🧑‍✈️ Driver: {(v.assigned_driver || v.driver).driver_name || (v.assigned_driver || v.driver).name}
                                        </div>
                                      )}
                                    </div>
                                    <span className="badge badge-green" style={{ fontSize: 11 }}>
                                      {v.status_display || 'Available'}
                                    </span>
                                  </div>
                                ))
                              )}
                            </div>
                          )}
                        </div>
                        <p style={{ margin: '6px 0 0', fontSize: 11, color: '#64748b' }}>
                          Only active, non-retired transport vehicles not assigned to another active route are shown.
                        </p>
                      </div>

                      {/* Selected Vehicle Details Preview Card */}
                      {selectedVehicleObj && (
                        <div style={{ background: '#eff6ff', padding: 16, borderRadius: 12, border: '1px solid #bfdbfe', marginBottom: 20, fontSize: 13 }}>
                          <div style={{ fontWeight: 700, color: '#1e40af', marginBottom: 8 }}>
                            bb🚍 Selected Vehicle Details:
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, fontSize: 12, color: '#1e293b' }}>
                            <div><strong>Registration Number:</strong> {selectedVehicleObj.registration_number}</div>
                            <div><strong>Vehicle Type:</strong> {selectedVehicleObj.vehicle_type_display}</div>
                            <div><strong>Seating Capacity:</strong> {selectedVehicleObj.seating_capacity} Seats</div>
                            <div><strong>Status:</strong> {selectedVehicleObj.status_display}</div>
                          </div>
                          <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid #dbeafe', fontSize: 12 }}>
                            <strong>Assigned Driver:</strong>{' '}
                            {(selectedVehicleObj.assigned_driver || selectedVehicleObj.driver) ? (
                              <span style={{ color: '#15803d', fontWeight: 600 }}>
                                🧑‍✈️ {(selectedVehicleObj.assigned_driver || selectedVehicleObj.driver).driver_name || (selectedVehicleObj.assigned_driver || selectedVehicleObj.driver).name} ({(selectedVehicleObj.assigned_driver || selectedVehicleObj.driver).employee_id || 'ID N/A'})
                              </span>
                            ) : (
                              <span style={{ color: '#64748b' }}>No driver assigned to this vehicle</span>
                            )}
                          </div>
                        </div>
                      )}

                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, borderTop: '1px solid #e2e8f0', paddingTop: 16 }}>
                        <button
                          type="button"
                          onClick={() => {
                            if (assignedVehicle) setIsEditingVehicleAssignment(false);
                            else setActiveDetailTab('overview');
                          }}
                          style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={assigningSubmitting || !selectedVehicleId}
                          className="driver-btn-primary"
                          style={{ width: 'auto', padding: '8px 24px' }}
                        >
                          {assigningSubmitting ? 'Assigning...' : assignedVehicle ? 'Confirm & Change Vehicle' : 'Assign Vehicle'}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              );
            })()}

            {/* TAB 4: ASSIGN STUDENT */}
            {activeDetailTab === 'assign_student' && selectedRoute && (
              <div>
                {/* Header Banner */}
                <div style={{ background: '#f8fafc', padding: 14, borderRadius: 10, border: '1px solid #e2e8f0', marginBottom: 20, fontSize: 13 }}>
                  <div style={{ fontWeight: 700, color: '#0f172a' }}>
                    🎓 Student Transport Enrollment — Route: {selectedRoute.route_number} ({selectedRoute.route_name})
                  </div>
                  <div style={{ color: '#64748b', fontSize: 12, marginTop: 4 }}>
                    Start: {selectedRoute.starting_point || 'Campus'} &bull; Vehicle: {selectedRoute.assigned_vehicle ? `🚍 ${selectedRoute.assigned_vehicle.registration_number} (${selectedRoute.assigned_vehicle.vehicle_type_display})` : '⚠️ No vehicle assigned'}
                  </div>
                </div>

                {!selectedRoute.assigned_vehicle && (
                  <div style={{ padding: 14, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, color: '#991b1b', fontSize: 13, marginBottom: 20 }}>
                    ⚠️ <strong>No Active Assigned Vehicle:</strong> This route currently has no active vehicle assigned. Please assign an active vehicle in the <strong>"Assign Vehicle"</strong> tab before assigning students.
                  </div>
                )}

                {/* Step 1: Search & Select Multiple Students (Checkboxes) */}
                <div style={{ marginBottom: 20, padding: 16, background: '#f8fafc', borderRadius: 12, border: '1px solid #cbd5e1' }}>
                  <label className="driver-label" style={{ marginBottom: 8, display: 'block', fontSize: 14, fontWeight: 700 }}>
                    Step 1: Search & Select Multiple Students (Checkboxes) *
                  </label>

                  <input
                    type="text"
                    placeholder="🔍 Search student by Reg No, Student Name, or Student ID..."
                    value={studentSearchQuery}
                    onChange={e => setStudentSearchQuery(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}
                    className="driver-input"
                    style={{ marginBottom: 10 }}
                  />

                  {/* Student Multi-Selection Search Results List */}
                  <div style={{ maxHeight: 220, overflowY: 'auto', background: '#fff', border: '1px solid #cbd5e1', borderRadius: 8 }}>
                    {searchingStudents ? (
                      <div style={{ padding: 12, textAlign: 'center', color: '#64748b', fontSize: 12 }}>Searching students in real-time...</div>
                    ) : studentSearchResults.length === 0 ? (
                      <div style={{ padding: 12, textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>
                        {studentSearchQuery ? 'No matching active students found.' : 'Type a student name or ID to search.'}
                      </div>
                    ) : (
                      studentSearchResults.map(s => {
                        const isChecked = selectedStudents.some(sel => sel.id === s.id);
                        return (
                          <div
                            key={s.id}
                            onClick={() => handleToggleStudentSelection(s)}
                            style={{
                              padding: '10px 14px',
                              borderBottom: '1px solid #f1f5f9',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justify: 'space-between',
                              background: isChecked ? '#eff6ff' : '#fff'
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => {}}
                                style={{ width: 16, height: 16, cursor: 'pointer' }}
                              />
                              <div>
                                <strong style={{ color: '#0f172a', fontSize: 13 }}>{s.full_name}</strong>
                                <span style={{ color: '#64748b', fontSize: 12, marginLeft: 8 }}>({s.student_id})</span>
                                <div style={{ fontSize: 11, color: '#475569', marginTop: 2 }}>
                                  Course: <strong>{s.course}</strong>
                                </div>
                              </div>
                            </div>

                            {s.has_active_transport && (
                              <span className="badge badge-yellow" style={{ fontSize: 11, marginLeft: 'auto', flexShrink: 0 }}>
                                Assigned ({s.active_transport?.route_number})
                              </span>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>

                  {/* Selected Students Summary Bar */}
                  {selectedStudents.length > 0 && (
                    <div style={{ marginTop: 12, padding: 14, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <div style={{ fontWeight: 700, color: '#1e40af', fontSize: 14 }}>
                          📋 Selected Students ({selectedStudents.length}):
                        </div>
                        <button
                          type="button"
                          onClick={() => setSelectedStudents([])}
                          style={{ padding: '4px 10px', background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12, fontWeight: 600, color: '#e11d48', cursor: 'pointer' }}
                        >
                          Clear All Selected
                        </button>
                      </div>

                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {selectedStudents.map(s => (
                          <span key={s.id} style={{ background: '#ffffff', border: '1px solid #bfdbfe', padding: '4px 10px', borderRadius: 20, fontSize: 12, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 6 }}>
                            <strong>{s.full_name}</strong> ({s.course})
                            <span
                              onClick={(e) => { e.stopPropagation(); handleToggleStudentSelection(s); }}
                              style={{ cursor: 'pointer', color: '#e11d48', fontWeight: 'bold', marginLeft: 4 }}
                            >
                              &times;
                            </span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Form Container for Step 2 and Direct Assignment */}
                <form onSubmit={handleAssignMultipleStudentsSubmit}>

                  {/* Step 2: Select Stop / Destination */}
                  {selectedStudents.length > 0 && (
                    <div style={{ marginBottom: 20, padding: 16, background: '#f8fafc', borderRadius: 12, border: '1px solid #cbd5e1' }}>
                      <label className="driver-label" style={{ marginBottom: 8, display: 'block', fontSize: 14, fontWeight: 700 }}>
                        Step 2: Select Destination Stop belonging to Route {selectedRoute.route_number} *
                      </label>

                      <select
                        required
                        value={selectedRouteStopId}
                        onChange={e => setSelectedRouteStopId(e.target.value)}
                        className="driver-input"
                      >
                        <option value="">Select Destination Stop ▼</option>
                        {(selectedRoute.stops || []).map(s => (
                          <option key={s.id} value={s.id}>
                            Stop #{s.stop_order}: {s.destination} — {s.distance_km} KM from Campus
                          </option>
                        ))}
                      </select>
                      <p style={{ margin: '4px 0 0', fontSize: 11, color: '#64748b' }}>
                        Multiple students can be assigned to the same destination stop based on vehicle capacity.
                      </p>
                    </div>
                  )}

                  {/* Direct Assignment Submit Button */}
                  {selectedStudents.length > 0 && selectedRouteStopId && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 20, marginBottom: 24 }}>
                      <button
                        type="submit"
                        disabled={assigningStudentSubmitting || !selectedRoute.assigned_vehicle}
                        className="driver-btn-primary"
                        style={{ width: 'auto', padding: '12px 30px', fontSize: 15, display: 'flex', alignItems: 'center', gap: 8 }}
                      >
                        <span>🎓</span> {assigningStudentSubmitting ? 'Enrolling Students...' : `Assign ${selectedStudents.length} Student(s) to Route`}
                      </button>
                    </div>
                  )}
                </form>

                {/* Section 5: Grouped Assigned Students Roster */}
                <div style={{ marginTop: 28, paddingTop: 20, borderTop: '2px solid #e2e8f0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <h4 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#0f172a' }}>
                      🎓 Enrolled Students on Route {selectedRoute.route_number} ({assignedStudents.length} Students Total)
                    </h4>
                  </div>

                  {loadingAssignedStudents ? (
                    <div style={{ padding: 20, textAlign: 'center', color: '#64748b' }}>Loading assigned students...</div>
                  ) : assignedStudents.length === 0 ? (
                    <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', background: '#f8fafc', borderRadius: 10, border: '1px dashed #cbd5e1', fontSize: 13 }}>
                      No students currently assigned to this route. Use the assignment section above to enroll students.
                    </div>
                  ) : (
                    (() => {
                      // Group assigned students by Course
                      const courseGroups = {};
                      assignedStudents.forEach(as => {
                        const courseName = as.course_display || as.course || 'Other Courses';
                        if (!courseGroups[courseName]) {
                          courseGroups[courseName] = [];
                        }
                        courseGroups[courseName].push(as);
                      });

                      const groupKeys = Object.keys(courseGroups);

                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                          {groupKeys.map(courseName => {
                            const groupList = courseGroups[courseName];
                            const totalGroupFees = groupList.reduce((sum, item) => sum + (parseFloat(item.transport_fee) || 0), 0);

                            return (
                              <div key={courseName} style={{ background: '#ffffff', borderRadius: 12, border: '1px solid #cbd5e1', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                                {/* Course Group Header Banner */}
                                <div style={{ background: '#f1f5f9', padding: '12px 18px', borderBottom: '1px solid #cbd5e1', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <span style={{ fontSize: 16 }}>📚</span>
                                    <h5 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
                                      {courseName}
                                    </h5>
                                    <span style={{ background: '#2563eb', color: '#fff', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 12 }}>
                                      {groupList.length} {groupList.length === 1 ? 'Student' : 'Students'}
                                    </span>
                                  </div>
                                  <div style={{ fontSize: 13, color: '#334155', fontWeight: 600 }}>
                                    Group Transport Fee Total: <strong style={{ color: '#15803d', fontSize: 14 }}>₹ {totalGroupFees.toLocaleString('en-IN')}</strong>
                                  </div>
                                </div>

                                {/* Course Group Table */}
                                <div className="table-container">
                                  <table className="data-table" style={{ fontSize: 13, margin: 0 }}>
                                    <thead>
                                      <tr style={{ background: '#fafafa' }}>
                                        <th>Name</th>
                                        <th>Reg no.</th>
                                        <th>Stop</th>
                                        <th>Fee</th>
                                        <th>Actions</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {groupList.map(as => (
                                        <tr key={as.id}>
                                          <td><strong style={{ color: '#0f172a' }}>{as.student_name}</strong></td>
                                          <td><span className="badge badge-blue">{as.student_registration}</span></td>
                                          <td><strong style={{ color: '#1e40af' }}>{as.destination}</strong></td>
                                          <td><strong style={{ color: '#15803d', fontSize: 14 }}>₹ {Number(as.transport_fee).toLocaleString('en-IN')}</strong></td>
                                          <td>
                                            <button
                                              type="button"
                                              onClick={() => handleUnassignStudent(as.id, as.student_name)}
                                              style={{ padding: '4px 10px', background: '#fff1f2', border: '1px solid #fecdd3', color: '#e11d48', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                                            >
                                              ❌ Unassign
                                            </button>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()
                  )}
                </div>
              </div>
            )}

          </div>
        </div>
      )}
    </div>
  );
};

export default TransportRoutesTab;
